import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, MailCheck } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#FFFFFF] border border-[#E7DFD5] px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300";

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 border-r border-[#E7DFD5] relative overflow-hidden">
        <div className="section-tag">§ Recover access</div>
        <div>
          <h1 className="font-display text-6xl xl:text-7xl leading-[0.9] tracking-tight mb-6">
            Forgot the <span className="italic text-[#B45309]">key</span>?
          </h1>
          <p className="text-[#78716C] text-lg leading-relaxed max-w-md">
            We'll send a reset link to your inbox. It works once and expires in an hour.
          </p>
        </div>
        <div className="font-display text-[9vw] leading-none text-[#44403C]">T.A.C.C.<span className="text-[#B45309]">.</span></div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
          className="w-full max-w-md pt-24 lg:pt-0"
          data-testid="forgot-password-page"
        >
          <div className="section-tag mb-4">Reset password</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            Send me a link.
          </h2>

          {sent ? (
            <div data-testid="forgot-password-sent" className="space-y-6">
              <div className="flex items-start gap-3 p-5 border border-[#F59E0B]/30 bg-[#F59E0B]/5">
                <MailCheck size={20} className="text-[#B45309] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-[#1C1917] font-medium mb-1">If that email is on file, a reset link is on its way.</p>
                  <p className="text-sm text-[#78716C] leading-relaxed">Check your inbox and spam folder. The link works once and expires in one hour.</p>
                </div>
              </div>
              <Link to="/login" className="tacc-link text-[#B45309] text-sm inline-flex items-center gap-1" data-testid="forgot-back-login">
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Email</label>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                    placeholder="you@email.com"
                  />
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-submit" className="btn-ember text-sm w-full justify-center">
                  {loading ? "Sending…" : "Send reset link"}
                  <ArrowUpRight size={16} />
                </button>
              </form>
              <p className="mt-8 text-sm text-[#78716C]">
                Remembered it?{" "}
                <Link to="/login" className="tacc-link text-[#B45309]" data-testid="forgot-to-login">Back to sign in</Link>
              </p>
            </>
          )}
          <Link to="/" className="mt-4 inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] hover:text-[#B45309] transition-colors">← Back to site</Link>
        </motion.div>
      </div>
    </main>
  );
}
